class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-cart-drawer-trigger], #cart-icon-bubble').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const drawerSection = parsedState.sections?.['cart-drawer'];
    if (drawerSection) {
      const freshDrawer = this.getSectionDOM(drawerSection, 'cart-drawer');
      const freshCartDrawer = freshDrawer?.querySelector('#CartDrawer');
      const currentCartDrawer = this.querySelector('#CartDrawer');
      if (freshCartDrawer && currentCartDrawer) currentCartDrawer.replaceWith(freshCartDrawer);
      this.classList.toggle('is-empty', freshDrawer?.classList.contains('is-empty') || false);
      this.dataset.upsellSource = freshDrawer?.dataset.upsellSource || this.dataset.upsellSource || '';
      this.dataset.upsellCartProductIds = freshDrawer?.dataset.upsellCartProductIds || '';
    }

    const iconHtml = parsedState.sections?.['cart-icon-bubble'];
    const icon = document.getElementById('cart-icon-bubble');
    if (iconHtml && icon) {
      const freshIcon = this.getSectionDOM(iconHtml);
      if (icon.classList.contains('ch-action')) {
        const sourceBadge = freshIcon?.querySelector('.cart-count-bubble');
        let badge = icon.querySelector('.ch-cart-badge');
        const count = sourceBadge?.querySelector('[aria-hidden="true"]')?.textContent?.trim();
        if (count) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'ch-cart-badge';
            icon.appendChild(badge);
          }
          badge.textContent = count;
        } else {
          badge?.remove();
        }
      } else if (freshIcon) {
        icon.innerHTML = freshIcon.innerHTML;
      }
    }

    requestAnimationFrame(() => {
      this.open();
      document.dispatchEvent(new CustomEvent('clearance:cart-drawer-rendered', { detail: { drawer: this } }));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Keep the header cart action bound to the drawer even after AJAX section renders.
// A single delegated capture listener avoids stale element handlers and duplicates.
if (!window.clearanceCartDrawerTriggerBound) {
  window.clearanceCartDrawerTriggerBound = true;
  document.addEventListener(
    'click',
    (event) => {
      const cartLink = event.target.closest('[data-cart-drawer-trigger], #cart-icon-bubble');
      if (!cartLink) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.open(cartLink);
    },
    true,
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.code.toUpperCase() !== 'SPACE' && event.code.toUpperCase() !== 'ENTER') return;
      const cartLink = event.target.closest?.('[data-cart-drawer-trigger], #cart-icon-bubble');
      if (!cartLink) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;
      event.preventDefault();
      drawer.open(cartLink);
    },
    true,
  );
}


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    const lineKey = input?.dataset.quantityLineKey;
    const lineVariantId = variantId || input?.dataset.quantityVariantId;
    if (!lineKey) return;

    this.enableLoading(line);
    const body = JSON.stringify({
      id: lineKey,
      quantity: Number(quantity),
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_change_url, { ...fetchConfig(), body })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok || state.errors) {
          const message = typeof state.errors === 'string' ? state.errors : window.cartStrings.error;
          throw new Error(message);
        }
        return state;
      })
      .then((state) => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections?.['cart-drawer']) drawer.renderContents(state);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: state,
          variantId: lineVariantId,
        });
      })
      .catch((error) => {
        console.error('Cart drawer quantity update failed:', error);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = error.message || window.cartStrings.error;
        if (input) input.value = input.getAttribute('value');
      })
      .finally(() => {
        document.querySelector('cart-drawer-items')?.disableLoading?.(line);
      });
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
